'use client'

/**
 * §19.10 persistence table + §19.10.1 the return state.
 *
 * Values, fades, gain and trim survive a refresh; the decoded audio cannot
 * (no File System Access API on this audience's browsers, so the source
 * file handle dies with the tab). This module is the "everything except
 * the audio" half of that contract — a small sessionStorage record keyed
 * off the file's name + byte length + duration, so a re-drop of the SAME
 * file re-attaches silently and a DIFFERENT file has to be asked about
 * explicitly rather than having stale trim points applied to it in silence.
 */

const STORAGE_KEY = 'tls-create-return-state'

export interface CreateReturnState {
  fileName: string
  fileSize: number
  fileDuration: number
  regionStart: number
  regionEnd: number
  fadeIn: number
  fadeOut: number
  fineTuneGainDb: number
  loudnessPreset: string
}

export function saveReturnState(state: CreateReturnState): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // Best-effort — losing the return-state record just means a refresh
    // behaves like a first visit instead of offering re-attachment.
  }
}

export function loadReturnState(): CreateReturnState | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed.fileName !== 'string') return null
    return parsed as CreateReturnState
  } catch {
    return null
  }
}

export function clearReturnState(): void {
  if (typeof window === 'undefined') return
  try { window.sessionStorage.removeItem(STORAGE_KEY) } catch { /* ignore */ }
}

/** §19.10.1's "matched on name + byte length + duration. All three must agree." */
export function matchesReturnState(state: CreateReturnState, file: File, durationSeconds: number): boolean {
  return (
    state.fileName === file.name &&
    state.fileSize === file.size &&
    Math.abs(state.fileDuration - durationSeconds) < 0.05
  )
}
